import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";

import { fetchFtmDocumentsTypes, updateFtmSkillApprove } from "../../service";
import DivSpinner from "../../../../components/divSpinner";
import { theme } from "../../../../theme";
import FtmDocuments from "./ftmDocuments/FtmDocuments";
import OffcanvasTemplate from "../../../../components/offcanvasTemplate";
import { commonSwal } from "../../../../components/swalAlert/swalAlert";

const DocumentTypes = ({ show, handleClose, skill, handleParentRefresh }) => {
    const { ftm_id } = useParams();

    const [loading, setLoading] = useState(true);
    const [documentTypes, setDocumentTypes] = useState(null);
    const [showDocuments, setShowDocuments] = useState(false);
    const [selectedDocumentTypeType, setSelectedDocumentTypeType] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const handleRefresh = () => setRefreshCounter((prev) => prev + 1);

    const handleOpenDocuments = useCallback((doc, type) => {
        setSelectedDocumentType(doc);
        setSelectedDocumentTypeType(type);
        setShowDocuments(true);
    }, []);

    const handleCloseDocuments = useCallback(() => {
        setShowDocuments(false);
    }, []);

    const getDocumentTypes = useCallback(async () => {
        if (!skill?.sub_skill?.sub_skill_id) return;

        try {
            setLoading(true);
            const response = await fetchFtmDocumentsTypes(ftm_id, skill.sub_skill.sub_skill_id);
            if (response?.status === "Success") {
                setDocumentTypes(response.data || null);
            }
        } catch (error) {
            console.error("Error fetching document types:", error);
        } finally {
            setLoading(false);
        }
    }, [ftm_id, skill?.sub_skill?.sub_skill_id]);

    useEffect(() => {
        if (show && skill) getDocumentTypes();
    }, [show, skill, refreshCounter, getDocumentTypes]);

    const getTypesArray = useCallback(
        () =>
            selectedDocumentTypeType === "parent"
                ? documentTypes?.skill_document_types
                : documentTypes?.sub_skill_document_types,
        [documentTypes, selectedDocumentTypeType]
    );

    const handleNextItem = useCallback(
        (id) => {
            const types = getTypesArray();
            const index = types?.findIndex((item) => item.documents_types_id === id);
            if (index !== -1 && index < types.length - 1) {
                setSelectedDocumentType(types[index + 1]);
            }
        },
        [getTypesArray]
    );

    const handlePrevItem = useCallback(
        (id) => {
            const types = getTypesArray();
            const index = types?.findIndex((item) => item.documents_types_id === id);
            if (index > 0) {
                setSelectedDocumentType(types[index - 1]);
            }
        },
        [getTypesArray]
    );

    /** ---------------- Sub-components ---------------- */
    const LoadingContent = useMemo(
        () => (
            <div style={{ height: `${window.innerHeight - 387}px` }}>
                <DivSpinner />
            </div>
        ),
        []
    );

    const DocumentList = useCallback(
        ({ title, documents, type }) => (
            <div className="col-md-12 my-5">
                <div className="card shadow-lg">
                    <span
                        className={`badge rounded-pill bg-${theme.main} text-light text-bold w-50 mt-n2 mx-auto`}
                    >
                        {title}
                    </span>
                    <div className="card-body text-lg-start text-center">
                        {documents?.length ? (
                            documents.map((doc) => {
                                const {
                                    documents_types_id,
                                    documents_types_name,
                                    documents_types_is_required,
                                    is_have_files,
                                    is_approve_document,
                                    document,
                                } = doc;

                                const expiryDate = document?.expiry_date
                                    ? new Date(document.expiry_date).toLocaleDateString()
                                    : "N/A";

                                const comment = document?.comment;

                                return (
                                    <div
                                        key={documents_types_id}
                                        className="d-flex flex-column p-2 text-dark rounded hover-row mb-1"
                                        onClick={() => handleOpenDocuments(doc, type)}
                                    >
                                        {/* Top Row */}
                                        <div className="d-flex justify-content-between align-items-center">
                                            {/* Left side */}
                                            <div className="d-flex align-items-center">
                                                <i className="material-icons my-auto">document_scanner</i>
                                                <span className="ps-3">{documents_types_name}</span>

                                                {documents_types_is_required === 1 && (
                                                    <i className="material-icons text-md opacity-10 text-danger ms-2">
                                                        star
                                                    </i>
                                                )}

                                                {is_have_files && (
                                                    <i
                                                        className={`material-icons text-md opacity-10 text-${theme.main} ms-2`}
                                                    >
                                                        library_books
                                                    </i>
                                                )}

                                                {comment && (
                                                    <Tooltip
                                                        title={comment}
                                                        arrow
                                                        placement="right"
                                                    >
                                                        <i
                                                            className={`material-icons text-md opacity-10 text-${theme.main} ms-2`}
                                                        >
                                                            chat_bubble
                                                        </i>
                                                    </Tooltip>
                                                )}
                                            </div>

                                            {/* Right side icon */}
                                            <i className={`material-icons cursor-pointer ${is_approve_document && `text-${theme.main}` }`} >
                                                {is_approve_document ? "verified" : "add_circle"}
                                            </i>
                                        </div>

                                        {/* Expiry Date Row */}
                                        <div className="d-flex align-items-center ps-0 mt-1 text-xs text-secondary">
                                            <strong className="me-1">Expiry Date:</strong>
                                            <span>{expiryDate}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="d-flex justify-content-lg-start justify-content-center p-2 text-dark">
                                <i className="material-icons my-auto">remove</i>
                                <span className="ps-3">No document types found.</span>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        ),
        [handleOpenDocuments]
    );

    const handleApproveSkill = (ftm_skill_id) => {
        commonSwal({
            notificationType: 'APPROVE_FTM_SKILL_TYPE',
            preConfirm: async () => {
                const response = await updateFtmSkillApprove(ftm_skill_id, ftm_id);
                return response;
            },
            isConfirmed: (result) => {
                handleParentRefresh();
                handleClose();
                toast.success(result?.value?.message);
            }
        })
    }

    return (
        <OffcanvasTemplate
            open={show}
            handleClose={handleClose}
            placement="end"
            title="Certificates"
            body={
                <section>
                    {loading ? (
                        LoadingContent
                    ) : (
                        <>
                            {/* Skill Header Card */}
                            <div className="card mb-2">
                                <div className="p-2 ps-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <p className="text-sm mb-0 text-capitalize">
                                                {documentTypes?.skill_name}
                                            </p>
                                            <h6 className="mb-0 d-flex align-items-center">
                                                {documentTypes?.sub_skill?.sub_skill_name}
                                                {documentTypes?.ftm_skill?.is_approve && (
                                                    <i className={`material-icons text-${theme.main} ms-2`}>
                                                        verified
                                                    </i>
                                                )}
                                            </h6>
                                        </div>
                                        <div
                                            className={`icon icon-md icon-shape bg-gradient-${theme.main} shadow-${theme.main} shadow text-center border-radius-lg`}
                                        >
                                            <i className="material-icons opacity-10">
                                                {documentTypes?.skill_web_app_icon}
                                            </i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Parent & Sub Document Lists */}
                            <DocumentList
                                title="Primary Documents"
                                documents={documentTypes?.skill_document_types}
                                type="parent"
                            />

                            <DocumentList
                                title="Supporting Documents"
                                documents={documentTypes?.sub_skill_document_types}
                                type="sub"
                            />

                            <FtmDocuments
                                show={showDocuments}
                                handleClose={handleCloseDocuments}
                                selectedDocumentType={selectedDocumentType}
                                selectedDocumentTypeType={selectedDocumentTypeType}
                                documentTypes={getTypesArray()}
                                parentRefresh={handleRefresh}
                                handleNextItem={handleNextItem}
                                handlePrevItem={handlePrevItem}
                            />
                        </>
                    )}
                </section>
            }
            footer={
                <div className="container">
                    <div className="row" style={{ marginTop: '10px' }}>
                        <div className="col">
                            <div className="d-grid gap-2 mb-3" >
                                <button
                                    type="button"
                                    className={`btn bg-gradient-${theme.main} btn-sm`}
                                    onClick={() => handleApproveSkill(documentTypes?.ftm_skill?.id)}
                                    disabled={documentTypes?.ftm_skill?.is_approve}
                                >
                                    <i className="material-icons text-lg me-2">
                                        {documentTypes?.skill_web_app_icon}
                                    </i>
                                    Approve {documentTypes?.sub_skill?.sub_skill_name}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            }
        />
    );
};

export default DocumentTypes;